import React, { useMemo, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Box, Text, useToken } from '@chakra-ui/react';
import { PieChart as RePieChart, Pie, Cell, Tooltip, Legend, ResponsiveContainer, Label as RechartsLabel } from 'recharts';

const COLORBLIND_PALETTE = [
  '#0072B2', '#E69F00', '#009E73', '#D55E00', '#56B4E9', '#CC79A7', '#F0E442', '#000000'
];

function PieChart({ data, title, height = 320, palette = 'colorblind', showLegend = true, showCenterLabel = true, usePatterns = false }) {
  const defaultKeys = ['green.500', 'orange.400', 'blue.500', 'purple.500', 'yellow.500'];
  const themePalette = useToken('colors', defaultKeys);
  const defaultPalette = palette === 'colorblind' ? COLORBLIND_PALETTE : themePalette;
  const [hiddenSegments, setHiddenSegments] = useState(() => new Set());
  const nameToIndex = useMemo(() => {
    const map = new Map();
    (data || []).forEach((d, idx) => map.set(d.name, idx));
    return map;
  }, [data]);
  const handleLegendClick = useCallback((entry) => {
    const label = entry && (entry.value || entry.payload?.value);
    const idx = nameToIndex.get(label);
    if (idx === undefined) return;
    setHiddenSegments(prev => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx); else next.add(idx);
      return next;
    });
  }, [nameToIndex]);
  const total = (data || []).reduce((sum, d) => sum + (d.value || 0), 0);
  if (!data || data.length === 0) {
    return <Text>No data available.</Text>;
  }
  return (
    <Box w="100%" h={{ base: '250px', md: `${height}px` }} bg="white" borderRadius="xl" border="1px solid" borderColor="gray.200" p={6}>
      {title && <Text fontWeight="bold" color="gray.700" mb={2}>{title}</Text>}
      <ResponsiveContainer width="100%" height="90%">
        <RePieChart>
          <Pie
            data={data}
            dataKey="value"
            nameKey="name"
            cx="50%"
            cy="50%"
            innerRadius={50}
            outerRadius={90}
            paddingAngle={2}
            label={false}
          >
            {usePatterns && (
              <defs>
                {data.map((entry, idx) => (
                  <pattern key={`pie-pat-${idx}`} id={`pie-pat-${idx}`} patternUnits="userSpaceOnUse" width="6" height="6" patternTransform="rotate(45)">
                    <rect width="6" height="6" fill={(entry.color || defaultPalette[idx % defaultPalette.length])} opacity="0.15" />
                    <line x1="0" y1="0" x2="0" y2="6" stroke={(entry.color || defaultPalette[idx % defaultPalette.length])} strokeWidth="2" />
                  </pattern>
                ))}
              </defs>
            )}
            {data.map((entry, idx) => {
              const color = entry.color || defaultPalette[idx % defaultPalette.length];
              const fill = usePatterns ? `url(#pie-pat-${idx})` : color;
              return (
                <Cell key={`cell-${idx}`} fill={fill} hide={hiddenSegments.has(idx)} />
              );
            })}
            {showCenterLabel && (
              <RechartsLabel
                value={`Total: ${total}`}
                position="center"
                fill="#2D3748"
                style={{ fontWeight: 600, fontSize: 14, textAnchor: 'middle' }}
              />
            )}
          </Pie>
          <Tooltip formatter={(value, name, props) => {
            const pct = props && props.payload && total ? ((props.payload.value / total) * 100).toFixed(1) + '%' : undefined;
            return [value, pct ? `${name} (${pct})` : name];
          }} />
          {showLegend && (
            <Legend
              verticalAlign="bottom"
              height={40}
              iconType="circle"
              wrapperStyle={{ paddingTop: '10px', fontSize: '12px' }}
              onClick={handleLegendClick}
              formatter={(value, entry) => {
                const idx = nameToIndex.get(value);
                const isHidden = idx !== undefined ? hiddenSegments.has(idx) : false;
                const payload = entry?.payload;
                const percent = payload && total ? ((payload.value / total) * 100).toFixed(1) : 0;
                const truncatedValue = value.length > 20 ? value.substring(0, 20) + '...' : value;
                return (
                  <span 
                    style={{ 
                      color: isHidden ? '#A0AEC0' : '#2D3748', 
                      textDecoration: isHidden ? 'line-through' : 'none',
                      fontSize: '11px'
                    }}
                    title={value}
                  >
                    {truncatedValue} ({percent}%)
                  </span>
                );
              }}
            />
          )}
        </RePieChart>
      </ResponsiveContainer>
    </Box>
  );
}

PieChart.propTypes = {
  data: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      value: PropTypes.number.isRequired,
      color: PropTypes.string,
    })
  ).isRequired,
  title: PropTypes.string,
  height: PropTypes.number,
  palette: PropTypes.oneOf(['colorblind', 'theme']),
  showLegend: PropTypes.bool,
  showCenterLabel: PropTypes.bool,
  usePatterns: PropTypes.bool,
};

PieChart.defaultProps = {
  title: '',
  height: 320,
  palette: 'colorblind',
  showLegend: true,
  showCenterLabel: true,
  usePatterns: true,
};

export default PieChart; 